import type { AppointmentTaker } from '../../marketplace/types';
import { getDayOfWeekInZone, getTimeInMinutesInZone, getTimeZoneAbbreviation } from '../../../utils/timezone';

const GRADIENT_PALETTE: ReadonlyArray<[string, string]> = [
  ['#007185', '#00a8b5'],
  ['#CC1B1B', '#e85d5d'],
  ['#2e5266', '#4a7c9b'],
  ['#5c4d7d', '#8b7ab8'],
  ['#1b6b4a', '#3d9970'],
  ['#8b4513', '#c17f3a'],
  ['#4a5568', '#718096'],
  ['#6b2d5c', '#a64d8a'],
  ['#1a365d', '#3182ce'],
  ['#744210', '#b7791f'],
];

/** Stable brand gradient per known booking slug */
const SLUG_GRADIENTS: Record<string, [string, string]> = {
  kevin: ['#CC1B1B', '#e85d5d'],
  dikshit: ['#2e5266', '#4a7c9b'],
  dikhit: ['#2e5266', '#4a7c9b'],
  karan: ['#744210', '#c7791f'],
  prashant: ['#5c4d7d', '#8b7ab8'],
  rudra: ['#1a365d', '#3182ce'],
  ujjval: ['#1b6b4a', '#3d9970'],
  sihag: ['#1b6b4a', '#2e8b57'],
  daksh: ['#6b3a7d', '#9b59b6'],
};

export interface ConsultantProfileExtra {
  featured?: boolean;
  languages?: string[];
  specialties?: string[];
  photoUrl?: string;
}

/** Curated public-facing metadata (until API exposes these fields). */
export const CONSULTANT_PROFILES: Record<string, ConsultantProfileExtra> = {
  kevin: {
    featured: true,
    languages: ['English'],
    specialties: ['General consultation', 'Immigration'],
  },
  dikshit: {
    languages: ['English', 'Hindi'],
    specialties: ['Study visa', 'Work permit'],
  },
  dikhit: {
    languages: ['English', 'Hindi'],
    specialties: ['Study visa', 'Work permit'],
  },
  karan: {
    languages: ['English', 'Hindi'],
    specialties: ['PR', 'Family sponsorship'],
  },
  prashant: {
    languages: ['English', 'Hindi'],
    specialties: ['Legal cases', 'Immigration', 'Traffic tickets'],
  },
  rudra: {
    languages: ['English', 'Hindi'],
    specialties: ['Criminal law', 'Matrimonial', 'Immigration'],
  },
  ujjval: {
    languages: ['English', 'Hindi'],
    specialties: ['Work permit', 'LMIA'],
  },
  sihag: {
    languages: ['English', 'Hindi'],
    specialties: ['Work permit', 'LMIA'],
  },
  daksh: {
    featured: false,
    languages: ['English', 'Hindi'],
    specialties: ['Tax advisory', 'Immigration'],
  },
};

/** Full name overrides for specific consultants */
export const FULL_NAME_OVERRIDES: Record<string, string> = {
  rudra: 'Adv Rudra Pratap Singh',
  prashant: 'Adv Prashant Katyan',
  karan: 'Karan Suneja',
  dikshit: 'Daksh',
  ujjval: 'Ujjval Sihag',
  sihag: 'Ujjval Sihag',
};
export type ConsultantRegion = 'canada' | 'india';
export type ConsultantSortOption = 'featured' | 'name' | 'availability';
export type ConsultantTypeFilter = 'all' | 'walk-in' | 'call';

function hashSlug(slug: string): number {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) >>> 0;
  }
  return hash;
}

export function getConsultantProfile(slug: string | null | undefined): ConsultantProfileExtra {
  if (!slug) return {};
  return CONSULTANT_PROFILES[slug.toLowerCase()] ?? {};
}

export function getConsultantShortName(displayName: string, firstName?: string): string {
  const trimmedFirst = firstName?.trim();
  if (trimmedFirst) return trimmedFirst;
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  return parts[0] ?? displayName;
}

export function getConsultantInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    const word = parts[0];
    return word.length >= 2 ? word.slice(0, 2).toUpperCase() : word.toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function getConsultantGradient(slug: string, unavailable = false): string {
  if (unavailable) {
    return 'linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%)';
  }
  const slugKey = slug.toLowerCase();
  const branded = SLUG_GRADIENTS[slugKey];
  if (branded) {
    const [from, to] = branded;
    return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
  }
  const index = hashSlug(slugKey) % GRADIENT_PALETTE.length;
  const [from, to] = GRADIENT_PALETTE[index];
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
}

export interface ConsultantFeeDisplay {
  amount: string;
  suffix?: string;
  isFree: boolean;
}

export function formatConsultantFee(fee: number, currency: string): ConsultantFeeDisplay {
  if (fee === 0) {
    if (currency === 'INR') {
      return { amount: 'INR 0.0', isFree: true };
    }
    return { amount: `${currency || 'CAD'} $0.00`, isFree: true };
  }
  if (currency === 'INR') {
    return {
      amount: `₹${fee.toLocaleString('en-IN')}`,
      suffix: 'consultation fee',
      isFree: false,
    };
  }
  return {
    amount: `${currency} $${fee.toFixed(2)}`,
    suffix: currency === 'CAD' ? '+ tax' : undefined,
    isFree: false,
  };
}

export function getAvailabilityLabel(taker: {
  allowWalkIn?: boolean;
  allowCall?: boolean;
}): string {
  const walkIn = taker.allowWalkIn !== false;
  const call = taker.allowCall !== false;
  if (!walkIn && !call) return 'Not currently booking';
  if (walkIn && call) return 'Call or walk-in available';
  if (call) return 'Phone call only';
  return 'Walk-in only';
}

export function isConsultantBookable(taker: {
  allowWalkIn?: boolean;
  allowCall?: boolean;
}): boolean {
  return taker.allowWalkIn !== false || taker.allowCall !== false;
}

function formatHour12(hour: number, minute: number): string {
  const h24 = hour % 24;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const suffix = h24 >= 12 ? 'PM' : 'AM';
  const mm = minute > 0 ? `:${String(minute).padStart(2, '0')}` : '';
  return `${h12}${mm} ${suffix}`;
}

const DEFAULT_BUSINESS_HOURS = { start: 11, startMinute: 0, end: 18, endMinute: 0 };

export function formatConsultantScheduleDays(taker: AppointmentTaker): string {
  const sat = taker.allowSaturday;
  const sun = taker.allowSunday;
  if (sat && sun) return 'Mon–Sun';
  if (sat) return 'Mon–Sat';
  if (sun) return 'Sun–Fri';
  return 'Mon–Fri';
}

export function formatConsultantHoursLabel(taker: AppointmentTaker, referenceDate = new Date()): string {
  const bh = taker.businessHours ?? DEFAULT_BUSINESS_HOURS;
  const tz = taker.timeZone || 'America/Toronto';
  const tzAbbr = getTimeZoneAbbreviation(referenceDate, tz);
  const days = formatConsultantScheduleDays(taker);
  const start = formatHour12(bh.start, bh.startMinute);
  const end = formatHour12(bh.end, bh.endMinute);
  return `${days} · ${start} – ${end}${tzAbbr ? ` ${tzAbbr}` : ''}`;
}

export function getConsultantTimezoneLabel(taker: AppointmentTaker, referenceDate = new Date()): string {
  const tz = taker.timeZone || 'America/Toronto';
  return getTimeZoneAbbreviation(referenceDate, tz);
}

export function getConsultantRegion(taker: AppointmentTaker): ConsultantRegion {
  return taker.timeZone === 'Asia/Kolkata' ? 'india' : 'canada';
}

export function getConsultantOfficeLabel(taker: AppointmentTaker): string | null {
  if (taker.allowWalkIn === false) return null;
  const city = taker.officeAddress?.city?.trim();
  if (city) return city;
  const name = taker.officeAddress?.name?.trim();
  return name || null;
}

function isDayBookable(dayOfWeek: number, taker: AppointmentTaker): boolean {
  if (dayOfWeek === 0 && !taker.allowSunday) return false;
  if (dayOfWeek === 6 && !taker.allowSaturday) return false;
  return true;
}

function getTodayCalendarInZone(timeZone: string, now = new Date()): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  return {
    year: parseInt(parts.find((p) => p.type === 'year')!.value, 10),
    month: parseInt(parts.find((p) => p.type === 'month')!.value, 10),
    day: parseInt(parts.find((p) => p.type === 'day')!.value, 10),
  };
}

function calendarToDate(year: number, month: number, day: number): Date {
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function formatShortWeekday(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(date);
}

/** Heuristic availability hint — no extra API calls on the picker page. */
export function getConsultantAvailabilityHint(taker: AppointmentTaker, now = new Date()): string {
  if (!isConsultantBookable(taker)) return 'Not currently booking';

  const tz = taker.timeZone || 'America/Toronto';
  const bh = taker.businessHours ?? DEFAULT_BUSINESS_HOURS;
  const today = getTodayCalendarInZone(tz, now);
  const todayDate = calendarToDate(today.year, today.month, today.day);
  const todayDow = getDayOfWeekInZone(todayDate, tz);
  const nowMinutes = getTimeInMinutesInZone(now, tz);
  const startMinutes = bh.start * 60 + bh.startMinute;
  const endMinutes = bh.end * 60 + bh.endMinute;

  if (isDayBookable(todayDow, taker)) {
    if (nowMinutes < startMinutes) {
      return `Available from ${formatHour12(bh.start, bh.startMinute)} today`;
    }
    if (nowMinutes < endMinutes - 30) {
      return 'Slots available today';
    }
  }

  for (let offset = 1; offset <= 14; offset++) {
    const candidate = new Date(todayDate);
    candidate.setDate(candidate.getDate() + offset);
    const dow = getDayOfWeekInZone(candidate, tz);
    if (!isDayBookable(dow, taker)) continue;
    const label = offset === 1 ? 'tomorrow' : formatShortWeekday(candidate, tz);
    return `Next available ${label}`;
  }

  return 'Check calendar for availability';
}

/** Lower score = sooner availability (for sorting). */
export function getConsultantAvailabilityScore(taker: AppointmentTaker, now = new Date()): number {
  const hint = getConsultantAvailabilityHint(taker, now);
  if (hint.includes('today') && !hint.includes('Next')) {
    if (hint.startsWith('Available from')) return 1;
    return 0;
  }
  if (hint.includes('tomorrow')) return 2;
  if (hint.startsWith('Next available')) return 3;
  return 99;
}

export function sortConsultantsForPicker(
  takers: AppointmentTaker[],
  sort: ConsultantSortOption = 'featured',
  now = new Date()
): AppointmentTaker[] {
  const list = [...takers];
  if (sort === 'name') {
    return list.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }
  if (sort === 'availability') {
    return list.sort((a, b) => {
      const diff = getConsultantAvailabilityScore(a, now) - getConsultantAvailabilityScore(b, now);
      return diff !== 0 ? diff : a.displayName.localeCompare(b.displayName);
    });
  }
  // featured: Kevin first, then alphabetical
  return list.sort((a, b) => {
    const aFeatured = getConsultantProfile(a.slug).featured ? 0 : 1;
    const bFeatured = getConsultantProfile(b.slug).featured ? 0 : 1;
    if (aFeatured !== bFeatured) return aFeatured - bFeatured;
    return a.displayName.localeCompare(b.displayName);
  });
}

export function filterConsultants(
  takers: AppointmentTaker[],
  options: {
    query?: string;
    typeFilter?: ConsultantTypeFilter;
    regionFilter?: ConsultantRegion | 'all';
  }
): AppointmentTaker[] {
  const query = options.query?.trim().toLowerCase() ?? '';
  const typeFilter = options.typeFilter ?? 'all';
  const regionFilter = options.regionFilter ?? 'all';

  return takers.filter((taker) => {
    if (typeFilter === 'walk-in' && taker.allowWalkIn === false) return false;
    if (typeFilter === 'call' && taker.allowCall === false) return false;
    if (regionFilter !== 'all' && getConsultantRegion(taker) !== regionFilter) return false;

    if (!query) return true;

    const profile = getConsultantProfile(taker.slug);
    const haystack = [
      taker.displayName,
      taker.slug ?? '',
      ...(profile.languages ?? []),
      ...(profile.specialties ?? []),
      getConsultantOfficeLabel(taker) ?? '',
    ]
      .join(' ')
      .toLowerCase();

    return haystack.includes(query);
  });
}
